import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useImageContext } from "../../contexts/ImageContext";
import {
  removeBackgroundSingle,
  getJobStatus,
  fetchCreationImageBlob,
} from "../../services/api/editor/background_removal";
import logger from "../../utils/logger";
import { registerLibraryUploadOnce } from "../../utils/registerLibraryUploadOnce";
import { useEditorStore } from "../../state/editorStore";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchCreationBlobWithRetry(token, cid, attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fetchCreationImageBlob(token, cid);
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) {
        await sleep(400 * (i + 1));
      }
    }
  }
  throw lastErr;
}

/**
 * Hook to handle background removal API calls and polling
 * @param {string | null} initialProcessedImage - Initial processed image (from location.state)
 * @param {(outputUrl: string, s3Url?: string) => void} [onBackgroundRemoved] - Called when a bg-removed image is ready (for storing and re-use)
 * @returns {{isLoading: boolean, processedImage: string | null, creationId: number | null, error: string | null, retry: () => void, setProcessedImage: (url: string | null) => void, setCreationId: (id: number | null) => void}}
 */
export const useImageProcessing = (
  initialProcessedImage = null,
  onBackgroundRemoved = null,
  { enabled: processingEnabled = true, ignoreProcessedGate = false } = {}
) => {
  const { selectedFile, clearImage, editorFlow } = useImageContext();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [processedImage, setProcessedImage] = useState(initialProcessedImage);
  const [creationId, setCreationId] = useState(null);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const processingStartedRef = useRef(false);
  const processedFileRef = useRef(null);
  const blobUrlRef = useRef(null);
  const clearImageRef = useRef(clearImage);
  clearImageRef.current = clearImage;
  const onBackgroundRemovedRef = useRef(onBackgroundRemoved);
  onBackgroundRemovedRef.current = onBackgroundRemoved;
  const ignoreProcessedGateRef = useRef(ignoreProcessedGate);
  ignoreProcessedGateRef.current = ignoreProcessedGate;
  const processingEnabledRef = useRef(processingEnabled);
  processingEnabledRef.current = processingEnabled;

  const revokeBlobUrl = () => {
    if (blobUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  const applyProcessedOutput = useCallback(async (token, outputUrl, cid, s3Url) => {
    if (cid && !Number.isNaN(cid)) {
      setCreationId(cid);
      useEditorStore.getState().setLineage({
        creationId: cid,
        transparentSourceId: cid,
        rootCreationId: cid,
      });
    }

    const isAlreadyBlob =
      typeof outputUrl === "string" &&
      (outputUrl.startsWith("blob:") || outputUrl.startsWith("data:"));

    if (isAlreadyBlob) {
      setProcessedImage(outputUrl);
      onBackgroundRemovedRef.current?.(outputUrl, s3Url || outputUrl);
      return;
    }

    if (cid && token) {
      try {
        const blob = await fetchCreationBlobWithRetry(token, cid);
        revokeBlobUrl();
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setProcessedImage(blobUrl);
        onBackgroundRemovedRef.current?.(blobUrl, s3Url || outputUrl);
        return;
      } catch (err) {
        logger.error(
          "Could not load creation image for editor pixel access; alpha picking may fail",
          err
        );
      }
    }

    setProcessedImage(outputUrl);
    onBackgroundRemovedRef.current?.(outputUrl, s3Url || outputUrl);
  }, []);

  // Update processedImage when initialProcessedImage changes
  useEffect(() => {
    if (initialProcessedImage) {
      setProcessedImage(initialProcessedImage);
    }
  }, [initialProcessedImage]);

  useEffect(() => {
    return () => {
      revokeBlobUrl();
    };
  }, []);

  // Poll job status until finished
  const pollJobStatus = async (jobId, token) => {
    const maxAttempts = 60;
    const pollInterval = 2000;
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        attempts++;

        if (attempts > maxAttempts) {
          reject(new Error("Processing timeout. Please try again."));
          return;
        }

        try {
          const status = await getJobStatus(token, jobId);

          if (status.status === "finished") {
            if (status.result?.success && status.result?.output_url) {
              resolve({
                outputUrl: status.result.output_url,
                creationId: status.result.creation_id ?? null,
              });
            } else {
              reject(new Error(status.result?.error || status.error || "Processing failed"));
            }
            return;
          }

          if (status.status === "failed") {
            reject(new Error(status.error || "Job failed"));
            return;
          }

          pollingIntervalRef.current = setTimeout(poll, pollInterval);
        } catch (error) {
          logger.error("Error polling job status", error);
          reject(new Error("Failed to check processing status. Please try again."));
        }
      };

      pollingIntervalRef.current = setTimeout(poll, pollInterval);
    });
  };

  useEffect(() => {
    const processImage = async () => {
      if (editorFlow === "passport") {
        processingStartedRef.current = false;
        processedFileRef.current = null;
        return;
      }

      if (!selectedFile) {
        processingStartedRef.current = false;
        processedFileRef.current = null;
        return;
      }

      if (processedFileRef.current === selectedFile) {
        logger.info("File already processed, skipping duplicate request");
        return;
      }

      if (processedImage && !ignoreProcessedGateRef.current) {
        processingStartedRef.current = false;
        processedFileRef.current = null;
        return;
      }

      if (processingStartedRef.current) {
        logger.info("Processing already in progress, skipping duplicate request");
        return;
      }

      processingStartedRef.current = true;
      processedFileRef.current = selectedFile;
      setIsLoading(true);
      setError(null);

      logger.info("Starting image processing", {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
      });

      try {
        const token = await getToken();
        if (!token) {
          throw new Error("Authentication required. Please sign in again.");
        }

        let lineage = null;
        try {
          const uploaded = await registerLibraryUploadOnce(token, selectedFile);
          const rootId = Number(
            uploaded?.root_creation_id ?? uploaded?.creation_id ?? uploaded?.id
          );
          if (Number.isFinite(rootId) && rootId > 0) {
            lineage = { root_creation_id: rootId };
          }
        } catch (uploadErr) {
          logger.warn(
            "Library upload failed; removing background without the stored original",
            uploadErr
          );
        }

        const response = await removeBackgroundSingle(
          token,
          selectedFile,
          "high",
          "png",
          lineage
        );

        logger.info("Background removal response:", {
          success: response.success,
          hasOutputUrl: !!response.output_url,
          hasJobId: !!response.job_id,
          creationId: response.creation_id,
        });

        if (response.output_url) {
          const cid =
            response.creation_id != null ? Number(response.creation_id) : null;
          await applyProcessedOutput(
            token,
            response.output_url,
            cid,
            response.s3_url
          );
          setIsLoading(false);
          logger.info("Image processed successfully");
          // Keep selectedFile + preview so Remove BG toggle OFF can restore
          // the upload. Mark this File as processed to avoid a re-run loop.
          processedFileRef.current = selectedFile;
          processingStartedRef.current = false;
          return;
        }

        if (response.job_id) {
          logger.info(`Job queued: ${response.job_id}`);

          try {
            const pollResult = await pollJobStatus(response.job_id, token);
            const cid =
              pollResult.creationId != null ? Number(pollResult.creationId) : null;
            await applyProcessedOutput(token, pollResult.outputUrl, cid);
            logger.info("Image processed successfully (queue mode)");
            processedFileRef.current = selectedFile;
          } catch (pollError) {
            throw pollError;
          } finally {
            setIsLoading(false);
            processingStartedRef.current = false;
          }
          return;
        }

        logger.error("Unexpected response format", { response });
        throw new Error(
          `Unexpected response from server: ${response.message || "No output_url or job_id received"}`
        );
      } catch (error) {
        logger.error("Error processing image", error);
        const errorMessage =
          error.message || "Failed to process image. Please try again.";
        setError(errorMessage);
        setIsLoading(false);
        processingStartedRef.current = false;
        processedFileRef.current = null;
        clearImageRef.current?.();

        setTimeout(() => {
          navigate("/dashboard", {
            state: { error: errorMessage },
          });
        }, 2000);
      }
    };

    if (
      processingEnabledRef.current &&
      selectedFile &&
      !processingStartedRef.current &&
      processedFileRef.current !== selectedFile
    ) {
      processImage();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    };
  }, [
    processingEnabled,
    selectedFile,
    getToken,
    navigate,
    editorFlow,
    applyProcessedOutput,
  ]);

  const retry = () => {
    setError(null);
    revokeBlobUrl();
    setProcessedImage(null);
    setCreationId(null);
    useEditorStore.getState().clearEditorSession();
    processingStartedRef.current = false;
    processedFileRef.current = null;
  };

  return {
    isLoading,
    processedImage,
    creationId,
    error,
    retry,
    setProcessedImage,
    setCreationId,
  };
};
